import React, { useState } from "react";
import { RotateCcw, ArrowLeftRight, AlignLeft, AlignCenter, AlignRight } from "lucide-react";
import { useBlockStyles } from "../context/BlockStylesContext";
import { useComposerContext } from "../context/ComposerContext";
import ColorPicker from "../ui/ColorPicker";

function ResetButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[10px] text-bookish-purple/40 hover:text-bookish-purple/70 flex items-center gap-0.5 transition-colors"
      title="Reset to global"
    >
      <RotateCcw size={10} />
      Global
    </button>
  );
}

function BookCardControls({ blockId }) {
  const el = typeof document !== "undefined" ? document.querySelector(`[data-bc-id="${blockId}"]`) : null;
  const bookCard = el?.querySelector("[data-book-layout]");
  const isBookCard = !!bookCard;

  const [layout, setLayout] = useState(bookCard?.dataset?.bookLayout || "left");
  const [border, setBorder] = useState(bookCard?.dataset?.bookBorder === "true");

  if (!isBookCard) return null;

  const toggleLayout = () => {
    const newLayout = layout === "left" ? "right" : "left";
    setLayout(newLayout);
    if (bookCard) {
      bookCard.dataset.bookLayout = newLayout;
      bookCard.style.flexDirection = newLayout === "right" ? "row-reverse" : "row";
    }
  };

  return (
    <div className="border-t border-bookish-purple/8 pt-3 space-y-2">
      <p className="text-xs font-medium text-bookish-purple/70">Book Card</p>
      <button
        type="button"
        onClick={toggleLayout}
        className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-bookish-purple/15 hover:bg-bookish-purple/5 transition-colors"
      >
        <ArrowLeftRight size={14} />
        Cover {layout === "left" ? "Left" : "Right"} — Toggle
      </button>
      <p className="text-[10px] text-bookish-purple/40">Click the cover image or buy button directly to edit them.</p>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={border} onChange={(event) => {
          const checked = event.target.checked;
          setBorder(checked);
          bookCard.dataset.bookBorder = String(checked);
          bookCard.style.border = checked ? "1px solid #d8d1c5" : "none";
        }} />
        Show a border around this book
      </label>
    </div>
  );
}

function ImageAlignControls({ blockId }) {
  const el = typeof document !== "undefined" ? document.querySelector(`[data-bc-id="${blockId}"]`) : null;
  const thumbRow = el?.querySelector("[data-thumb-row]");

  if (!thumbRow) return null;

  const currentAlign = thumbRow?.dataset?.align || "center";

  const setAlign = (align) => {
    if (!thumbRow) return;
    thumbRow.dataset.align = align;
    const justifyMap = { left: "flex-start", center: "center", right: "flex-end" };
    thumbRow.style.justifyContent = justifyMap[align] || "center";
  };

  return (
    <div className="border-t border-bookish-purple/8 pt-3 space-y-2">
      <p className="text-xs font-medium text-bookish-purple/70">Image Gallery</p>
      <div>
        <label className="text-xs text-bookish-purple/60 block mb-1">Alignment</label>
        <div className="flex gap-1">
          {[
            { val: "left", icon: AlignLeft },
            { val: "center", icon: AlignCenter },
            { val: "right", icon: AlignRight },
          ].map(({ val, icon: Icon }) => (
            <button
              key={val}
              type="button"
              onClick={() => setAlign(val)}
              className={`p-1.5 rounded-lg border transition-colors ${currentAlign === val ? "border-bookish-purple bg-bookish-purple/10" : "border-bookish-purple/15 hover:bg-bookish-purple/5"}`}
            >
              <Icon size={14} />
            </button>
          ))}
        </div>
      </div>
      <p className="text-[10px] text-bookish-purple/40">Click individual images to edit, replace, or add links.</p>
    </div>
  );
}

export default function BlockSettingsPanel({ blockId = null, onClose = null }) {
  const {
    activeAdvancedBlockId,
    closeAdvancedBlock,
    blockStyles,
    updateBlockStyle,
    resetBlockStyle,
    resetAllBlockStyles,
  } = useBlockStyles();
  const { cardBg, textColor, accentColor, fontFamily, fontSize } = useComposerContext();

  const targetBlockId = blockId || activeAdvancedBlockId;

  if (!targetBlockId) {
    return (
      <div className="text-center py-8 text-bookish-purple/40">
        <p className="text-xs">Click a block in the editor to customize its style independently.</p>
      </div>
    );
  }

  const overrides = blockStyles[targetBlockId] || {};

  const blockEl = typeof document !== "undefined" ? document.querySelector(`[data-bc-id="${targetBlockId}"]`) : null;
  const hasButton = !!blockEl?.querySelector("[data-bc-button]");

  const applyToDOM = (blockId, prop, value) => {
    const el = document.querySelector(`[data-bc-id="${blockId}"]`);
    if (!el) return;

    switch (prop) {
      case "backgroundColor":
        el.style.backgroundColor = value;
        break;
      case "textColor":
        el.style.color = value;
        el.querySelectorAll("p, h1, h2, h3, span, div, a").forEach((child) => {
          if (child.hasAttribute("data-bc-drag") || child.hasAttribute("data-bc-label") || child.hasAttribute("data-bc-hint") || child.hasAttribute("data-bc-lock")) return;
          if (child.hasAttribute("data-bc-button")) return;
          child.style.color = value;
        });
        break;
      case "buttonTextColor":
        el.querySelectorAll("[data-bc-button]").forEach((btn) => {
          btn.style.color = value;
        });
        break;
      case "accentColor":
        el.querySelectorAll("[data-bc-accent]").forEach((accentEl) => {
          accentEl.style.background = value;
          if (accentEl.hasAttribute("data-header-bg")) {
            accentEl.setAttribute("data-header-bg", value);
          }
          if (accentEl.style.borderLeftColor || accentEl.style.borderLeft) {
            accentEl.style.borderLeftColor = value;
          }
        });
        break;
      case "padding":
        el.style.padding = value;
        break;
      case "fontFamily":
        el.style.fontFamily = value;
        break;
      case "fontSize":
        el.style.fontSize = value;
        break;
      default:
        break;
    }
  };

  const handleChange = (prop, value) => {
    updateBlockStyle(targetBlockId, { [prop]: value });
    applyToDOM(targetBlockId, prop, value);
  };

  const handleReset = (prop) => {
    resetBlockStyle(targetBlockId, prop);
    const globals = { backgroundColor: cardBg, textColor, accentColor, buttonTextColor: "#ffffff", padding: "22px", fontFamily, fontSize };
    applyToDOM(targetBlockId, prop, globals[prop]);
  };

  const handleResetAll = () => {
    resetAllBlockStyles(targetBlockId);
    ["backgroundColor", "textColor", "accentColor", "buttonTextColor", "padding", "fontFamily", "fontSize"].forEach((prop) => {
      const globals = { backgroundColor: cardBg, textColor, accentColor, buttonTextColor: "#ffffff", padding: "22px", fontFamily, fontSize };
      applyToDOM(targetBlockId, prop, globals[prop]);
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <p className="text-xs font-medium text-bookish-purple/70">Block Overrides</p>
          <button
            type="button"
            onClick={onClose || closeAdvancedBlock}
            className="text-[10px] text-bookish-purple/40 hover:text-bookish-purple/70 transition-colors"
          >
            Close
          </button>
        </div>
        <button
          type="button"
          onClick={handleResetAll}
          className="text-[10px] text-red-400 hover:text-red-600 flex items-center gap-0.5 transition-colors"
        >
          <RotateCcw size={10} />
          Reset All
        </button>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-bookish-purple/60">Background</label>
          {overrides.backgroundColor && <ResetButton onClick={() => handleReset("backgroundColor")} />}
        </div>
        <ColorPicker
          label=""
          value={overrides.backgroundColor || cardBg}
          onChange={(v) => handleChange("backgroundColor", v)}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-bookish-purple/60">Text Color</label>
          {overrides.textColor && <ResetButton onClick={() => handleReset("textColor")} />}
        </div>
        <ColorPicker
          label=""
          value={overrides.textColor || textColor}
          onChange={(v) => handleChange("textColor", v)}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-bookish-purple/60">Accent Color</label>
          {overrides.accentColor && <ResetButton onClick={() => handleReset("accentColor")} />}
        </div>
        <ColorPicker
          label=""
          value={overrides.accentColor || accentColor}
          onChange={(v) => handleChange("accentColor", v)}
        />
      </div>

      {hasButton && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-bookish-purple/60">Button Text</label>
            {overrides.buttonTextColor && <ResetButton onClick={() => handleReset("buttonTextColor")} />}
          </div>
          <ColorPicker
            label=""
            value={overrides.buttonTextColor || "#ffffff"}
            onChange={(v) => handleChange("buttonTextColor", v)}
          />
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-bookish-purple/60">Padding</label>
          {overrides.padding && <ResetButton onClick={() => handleReset("padding")} />}
        </div>
        <select
          value={overrides.padding || "22px"}
          onChange={(e) => handleChange("padding", e.target.value)}
          className="w-full border border-bookish-purple/15 rounded-lg px-2.5 py-1.5 text-sm bg-white"
        >
          <option value="8px">Compact</option>
          <option value="14px">Small</option>
          <option value="22px">Normal</option>
          <option value="32px">Spacious</option>
          <option value="44px">Large</option>
        </select>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-bookish-purple/60">Font Family</label>
          {overrides.fontFamily && <ResetButton onClick={() => handleReset("fontFamily")} />}
        </div>
        <select
          value={overrides.fontFamily || fontFamily}
          onChange={(e) => handleChange("fontFamily", e.target.value)}
          className="w-full border border-bookish-purple/15 rounded-lg px-2.5 py-1.5 text-sm bg-white"
        >
          <option value="Georgia, serif">Georgia</option>
          <option value="Times New Roman, serif">Times New Roman</option>
          <option value="Arial, sans-serif">Arial</option>
          <option value="Verdana, sans-serif">Verdana</option>
          <option value="Trebuchet MS, sans-serif">Trebuchet MS</option>
        </select>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-bookish-purple/60">Text Size</label>
          {overrides.fontSize && <ResetButton onClick={() => handleReset("fontSize")} />}
        </div>
        <select
          value={overrides.fontSize || fontSize}
          onChange={(e) => handleChange("fontSize", e.target.value)}
          className="w-full border border-bookish-purple/15 rounded-lg px-2.5 py-1.5 text-sm bg-white"
        >
          <option value="12px">Small (12px)</option>
          <option value="14px">Normal (14px)</option>
          <option value="16px">Medium (16px)</option>
          <option value="18px">Large (18px)</option>
          <option value="20px">Extra Large (20px)</option>
          <option value="24px">Heading (24px)</option>
          <option value="28px">Heading+ (28px)</option>
          <option value="32px">Display (32px)</option>
          <option value="36px">Display+ (36px)</option>
        </select>
      </div>

      {/* Image gallery alignment (kept in sidebar since it's layout, not per-image) */}
      <ImageAlignControls blockId={targetBlockId} />

      {/* Book card layout toggle */}
      <BookCardControls key={targetBlockId} blockId={targetBlockId} />
    </div>
  );
}
